/**
 * `Funnel` and `Waterfall` (D529): the two charts that tell how a quantity shrinks or moves step by step. Both are SVG (so
 * the PNG export holds them whole), reachable by keyboard, with the conversion / the signed change printed in text.
 */
import { useRef, type ReactElement } from 'react';
import { bandScale, clamp, funnelSteps, isFiniteNumber, linearScale, roundCoord, valueDomain, waterfallSteps, type FunnelInput, type WaterfallInput } from '@vello/charts-core';
import {
  AXIS_CHAR_WIDTH,
  ChartToolbar,
  chartState,
  chosenAttr,
  emitSelect,
  formatPercentDefault,
  frameMetrics,
  pointerPosition,
  truncate,
  type ChartSelectProps,
} from './bi-kit';
import type { ChartCsv, ChartExportProps } from './export';
import { ChartLive, ChartTooltip, cascade, colorOf, cx, defaultFormatValue, distinctTicks, joinSummary, toneColor, useActivePoint, useElementWidth, useMotionState } from './kit';
import type { ChartBaseProps } from './line-chart';

const FALLBACK_WIDTH = 480;

// ── Funnel ─────────────────────────────────────────────────────────────────────────────────────────────────────────

/** The words the funnel's tooltip and summary need (injected, localized). */
export interface FunnelWords {
  /** "of the previous step" — follows the conversion in the summary. */
  readonly fromPrevious: string;
  readonly fromFirst?: string;
  readonly dropOff?: string;
  /** "Overall conversion" — shown under the funnel with the first → last rate. */
  readonly overall?: string;
}

export interface FunnelProps extends ChartBaseProps, ChartSelectProps, ChartExportProps {
  readonly steps: readonly FunnelInput[];
  readonly words: FunnelWords;
  /** Format a rate (a fraction; default a whole percent). */
  readonly formatShare?: (fraction: number) => string;
}

const FUNNEL_ROW = 52;

/** Centered bars, one per step, widest first: label, count and the conversion from the previous step. `onSelect` reports the step key. */
export function Funnel(props: FunnelProps): ReactElement {
  const height = Math.max(1, props.steps.length) * FUNNEL_ROW;
  return chartState(props, height, props.steps.some((s) => isFiniteNumber(s.value) && s.value > 0)) ?? <FunnelPlot {...props} />;
}

function FunnelPlot(props: FunnelProps): ReactElement {
  const funnel = funnelSteps(props.steps);
  const steps = funnel.steps;
  const count = steps.length;
  const format = props.formatValue ?? defaultFormatValue;
  const formatShare = props.formatShare ?? formatPercentDefault;
  const ref = useRef<HTMLDivElement>(null);
  const width = useElementWidth(ref, FALLBACK_WIDTH);
  const motion = useMotionState(props.animate);
  const active = useActivePoint(count, props.onSelect !== undefined ? (i) => emitSelect(props, steps[i]?.key ?? '', props.dimension ?? 'step') : undefined);
  const height = count * FUNNEL_ROW;
  const pad = 4;
  const barArea = Math.max(40, width - 2 * pad);
  const rightText = (i: number): string => {
    const s = steps[i];
    if (s === undefined) return '';
    return `${format(s.value)}${s.fromPrevious !== null ? ` · ${formatShare(s.fromPrevious)}` : ''}`;
  };
  const indexAt = (y: number): number => clamp(Math.floor(y / FUNNEL_ROW), 0, count - 1);

  const summary =
    props.ariaLabel ??
    joinSummary([
      props.title,
      steps
        .map((s) => `${s.label}: ${format(s.value)}${s.fromPrevious !== null ? ` (${formatShare(s.fromPrevious)} ${props.words.fromPrevious})` : ''}`)
        .join('; '),
      props.words.overall !== undefined && funnel.overall !== null ? `${props.words.overall}: ${formatShare(funnel.overall)}` : undefined,
    ]);
  const csv = (): ChartCsv => ({
    header: ['', ...(props.title !== undefined ? [props.title] : ['value']), props.words.fromPrevious, props.words.fromFirst ?? '', props.words.dropOff ?? ''],
    rows: steps.map((s) => [s.label, s.value, s.fromPrevious, s.fromFirst, s.dropOff]),
  });
  const a = active.index;
  const activeStep = a === null ? undefined : steps[a];
  const rows =
    activeStep === undefined
      ? []
      : [
          { key: 'v', value: format(activeStep.value) },
          ...(activeStep.fromPrevious !== null ? [{ key: 'p', label: props.words.fromPrevious, value: formatShare(activeStep.fromPrevious) }] : []),
          ...(props.words.fromFirst !== undefined && activeStep.fromFirst !== null ? [{ key: 'f', label: props.words.fromFirst, value: formatShare(activeStep.fromFirst) }] : []),
          ...(props.words.dropOff !== undefined && activeStep.dropOff > 0 ? [{ key: 'd', label: props.words.dropOff, value: format(activeStep.dropOff) }] : []),
        ];

  return (
    <div className={cx('vello-chart', 'vello-chart--funnel', props.className)} data-motion={motion}>
      <ChartToolbar props={props} title={props.title} csv={csv} />
      <div
        ref={ref}
        className="vello-chart__plot vello-chart__focus"
        role="img"
        aria-label={summary}
        tabIndex={0}
        onKeyDown={active.onKeyDown}
        onFocus={active.onFocus}
        onBlur={active.onBlur}
      >
        <svg
          className="vello-chart__svg"
          width={width}
          height={height}
          viewBox={`0 0 ${width} ${height}`}
          onPointerMove={(e) => active.set(indexAt(pointerPosition(e).y))}
          onPointerLeave={active.onPointerLeave}
          onClick={(e) => {
            if (props.onSelect !== undefined) emitSelect(props, steps[indexAt(pointerPosition(e).y)]?.key ?? '', props.dimension ?? 'step');
          }}
        >
          {steps.map((s, i) => {
            const top = i * FUNNEL_ROW;
            const w = s.value > 0 ? Math.max(2, s.widthShare * barArea) : 2;
            const right = rightText(i);
            const labelChars = Math.floor((barArea - right.length * AXIS_CHAR_WIDTH - 16) / 6.4);
            return (
              <g key={s.key} {...chosenAttr(props, s.key)} data-active={a === null ? undefined : a === i ? 'true' : 'false'}>
                <text className="vello-chart__funnel-label" x={pad} y={top + 16}>
                  {truncate(s.label, labelChars)}
                </text>
                <text className="vello-chart__funnel-figures tnum" x={pad + barArea} y={top + 16} textAnchor="end">
                  {right}
                </text>
                <rect className="vello-chart__bar vello-chart__extend" x={roundCoord(pad + (barArea - w) / 2)} y={top + 24} width={roundCoord(w)} height={20} rx={4} fill={colorOf(0)} style={cascade(i)} />
              </g>
            );
          })}
        </svg>
        {a !== null && activeStep !== undefined && <ChartTooltip x={width / 2} y={a * FUNNEL_ROW + 22} width={width} heading={activeStep.label} rows={rows} />}
      </div>
      <ChartLive text={activeStep === undefined ? '' : `${activeStep.label}: ${rightText(a ?? 0)}`} />
      {props.words.overall !== undefined && funnel.overall !== null && (
        <p className="vello-chart__note">
          <span>{props.words.overall}</span> <span className="tnum">{formatShare(funnel.overall)}</span>
        </p>
      )}
    </div>
  );
}

// ── Waterfall ──────────────────────────────────────────────────────────────────────────────────────────────────────

export interface WaterfallProps extends ChartBaseProps, ChartSelectProps, ChartExportProps {
  readonly steps: readonly WaterfallInput[];
  /** Format a change with its sign (default: `+` / `−` before the formatted amount). The sign is the non-colour signal. */
  readonly formatDelta?: (value: number) => string;
  /** The word for a total that does not match the running sum, added to that step (localized). */
  readonly mismatchLabel?: string;
  readonly height?: number;
  readonly showGrid?: boolean;
  readonly showAxis?: boolean;
  readonly formatAxis?: (value: number) => string;
}

const TOP = 22;

/** Floating bars from a start through increases and decreases to a total, joined by connector lines. `onSelect` reports the step key. */
export function Waterfall(props: WaterfallProps): ReactElement {
  const height = props.height ?? 240;
  return chartState(props, height, props.steps.some((s) => isFiniteNumber(s.value))) ?? <WaterfallPlot {...props} height={height} />;
}

function WaterfallPlot(props: WaterfallProps & { height: number }): ReactElement {
  const wf = waterfallSteps(props.steps);
  const steps = wf.steps;
  const count = steps.length;
  const format = props.formatValue ?? defaultFormatValue;
  const formatAxis = props.formatAxis ?? format;
  const formatDelta = props.formatDelta ?? ((v: number) => `${v < 0 ? '−' : '+'}${format(Math.abs(v))}`);
  const showAxis = props.showAxis ?? true;
  const showGrid = props.showGrid ?? true;
  const ref = useRef<HTMLDivElement>(null);
  const width = useElementWidth(ref, FALLBACK_WIDTH);
  const motion = useMotionState(props.animate);
  const active = useActivePoint(count, props.onSelect !== undefined ? (i) => emitSelect(props, steps[i]?.key ?? '', props.dimension ?? 'step') : undefined);
  const domain = distinctTicks(valueDomain([wf.min, wf.max], { includeZero: true, tickCount: 4 }), formatAxis);
  const tickText = domain.ticks.map(formatAxis);
  const f = frameMetrics({ width, height: props.height, tickText, showAxis, top: TOP });
  const y = linearScale(domain.domain, [f.baselineY, f.top]);
  const band = bandScale(count, [f.left, f.left + f.plotWidth], { padding: 0.3 });
  const stride = Math.max(1, Math.ceil(56 / Math.max(band.step, 1)));
  const labelOf = (i: number): string => {
    const s = steps[i];
    return s === undefined ? '' : s.kind === 'total' ? format(s.end) : formatDelta(s.delta);
  };

  const summary =
    props.ariaLabel ??
    joinSummary([
      props.title,
      steps.map((s, i) => `${s.label}${!s.reconciles && props.mismatchLabel !== undefined ? ` (${props.mismatchLabel})` : ''}: ${labelOf(i)}`).join('; '),
    ]);
  const csv = (): ChartCsv => ({ header: ['', 'change', 'from', 'to'], rows: steps.map((s) => [s.label, s.delta, s.start, s.end]) });
  const a = active.index;
  const activeStep = a === null ? undefined : steps[a];

  return (
    <div className={cx('vello-chart', 'vello-chart--waterfall', props.className)} data-motion={motion}>
      <ChartToolbar props={props} title={props.title} csv={csv} />
      <div
        ref={ref}
        className="vello-chart__plot vello-chart__focus"
        role="img"
        aria-label={summary}
        tabIndex={0}
        onKeyDown={active.onKeyDown}
        onFocus={active.onFocus}
        onBlur={active.onBlur}
      >
        <svg
          className="vello-chart__svg"
          width={width}
          height={props.height}
          viewBox={`0 0 ${width} ${props.height}`}
          onClick={(e) => active.activateFromPointer(e, (x) => band.indexAt(x))}
          onPointerMove={(e) => active.fromPointer(e, (x) => band.indexAt(x))}
          onPointerLeave={active.onPointerLeave}
        >
          {showGrid && domain.ticks.map((t) => <line key={`g${t}`} className={t === 0 ? 'vello-chart__baseline' : 'vello-chart__grid'} x1={f.left} x2={f.left + f.plotWidth} y1={y(t)} y2={y(t)} />)}
          {showAxis &&
            domain.ticks.map((t, i) => (
              <text key={`t${t}`} className="vello-chart__tick tnum" x={f.left - 8} y={y(t)} dy="0.32em" textAnchor="end">
                {tickText[i]}
              </text>
            ))}
          {steps.map((s, i) => {
            const lo = Math.min(y(s.start), y(s.end));
            const h = Math.max(2, Math.abs(y(s.start) - y(s.end)));
            const next = steps[i + 1];
            const colour = s.direction === 'total' ? colorOf(0) : s.direction === 'up' ? toneColor('success') : toneColor('danger');
            const labelAbove = s.direction !== 'down' || s.kind === 'total';
            return (
              <g key={s.key} {...chosenAttr(props, s.key)} data-active={a === null ? undefined : a === i ? 'true' : 'false'} data-direction={s.direction} data-mismatch={!s.reconciles ? 'true' : undefined}>
                <rect className="vello-chart__bar vello-chart__rise" x={band.start(i)} y={roundCoord(lo)} width={band.bandwidth} height={roundCoord(h)} rx={2} fill={colour} style={cascade(i)} data-hatch={!s.reconciles ? 'true' : undefined} />
                {next !== undefined && <line className="vello-chart__connector" x1={band.start(i) + band.bandwidth} x2={band.start(i + 1)} y1={y(s.end)} y2={y(s.end)} />}
                <text className="vello-chart__value tnum" x={band.center(i)} y={labelAbove ? lo - 6 : lo + h + 14} textAnchor="middle">
                  {labelOf(i)}
                </text>
              </g>
            );
          })}
          {showAxis &&
            steps.map((s, i) =>
              i % stride === 0 ? (
                <text key={`x${i}`} className="vello-chart__tick" x={band.center(i)} y={f.baselineY + 18} textAnchor="middle">
                  {truncate(s.label, Math.floor(band.step * stride / 6.2))}
                </text>
              ) : null,
            )}
        </svg>
        {a !== null && activeStep !== undefined && (
          <ChartTooltip
            x={band.center(a)}
            y={Math.min(y(activeStep.start), y(activeStep.end))}
            width={width}
            heading={activeStep.label}
            rows={[
              { key: 'v', value: activeStep.kind === 'total' ? format(activeStep.end) : formatDelta(activeStep.delta) },
              ...(activeStep.kind === 'delta' ? [{ key: 'to', value: format(activeStep.end) }] : []),
              ...(!activeStep.reconciles && props.mismatchLabel !== undefined ? [{ key: 'm', value: props.mismatchLabel }] : []),
            ]}
          />
        )}
      </div>
      <ChartLive text={activeStep === undefined ? '' : `${activeStep.label}: ${labelOf(a ?? 0)}`} />
    </div>
  );
}
